'use client';

import React, { useEffect, useRef, useState } from 'react';

interface VisibleVideoProps {
	webmSrc: string;
	mp4FallbackSrc?: string; // optional fallback
	className?: string;
}

const VisibleVideo: React.FC<VisibleVideoProps> = ({ webmSrc, mp4FallbackSrc, className }) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const videoElement = videoRef.current;
		if (!videoElement) return;

		// Intersection Observer callback
		const observerCallback: IntersectionObserverCallback = (entries) => {
			const [entry] = entries;
			// Intersection ratio >= 0.75 => at least 75% of the video is visible
			if (entry.intersectionRatio >= 0.75) {
				setInView(true);
			} else {
				setInView(false);
			}
		};

		// Observe with a threshold of 0.75 (75% visible)
		const observerOptions = {
			root: null,
			rootMargin: '0px',
			threshold: [0, 0.75]
		};
		const observer = new IntersectionObserver(observerCallback, observerOptions);
		observer.observe(videoElement);

		return () => {
			observer.unobserve(videoElement);
			observer.disconnect();
		};
	}, []);

	useEffect(() => {
		const videoElement = videoRef.current;
		if (!videoElement) return;

		if (inView) {
			// Rewind to the beginning and play
			videoElement.currentTime = 0;
			videoElement.play().catch(() => {
				// Some browsers block autoplay if not muted,
				// but we have it muted, so typically no issue.
			});
		} else {
			// Pause and reset
			videoElement.pause();
			videoElement.currentTime = 0;
		}
	}, [inView]);

	return (
		<video
			ref={videoRef}
			className={className}
			muted
			loop
			playsInline
			style={{
				width: '100%',
				height: 'auto',
				objectFit: 'cover'
			}}
		>
			{/* Primary WebM source */}
			<source src={webmSrc} type="video/webm" />
			{/* Optional fallback MP4 */}
			{mp4FallbackSrc && <source src={mp4FallbackSrc} type="video/mp4" />}
			Your browser does not support the video tag.
		</video>
	);
};

export default VisibleVideo;
